/**
 * Zoom-based lake visibility configuration.
 *
 * At lower zoom levels (more zoomed out), smaller lakes are hidden
 * to reduce texture complexity. As zoom increases, more lakes appear.
 *
 * Natural Earth scalerank: 0 = most important (Great Lakes, Lake Baikal),
 * higher values = less important.
 */

import type { LakeData } from '../types/features'

/**
 * Minimum scalerank to show at each zoom level.
 * Lakes with scalerank <= threshold will be shown.
 *
 * At zoom 5: Only show major lakes (scalerank 0-2)
 * At zoom 6: Show more lakes (scalerank 0-4)
 * At zoom 7+: Show most lakes (scalerank 0-6)
 */
const SCALERANK_THRESHOLDS: Record<number, number> = {
  5: 2,   // Only major lakes (Great Lakes, Baikal, Victoria, etc.)
  6: 4,   // Medium lakes
  7: 6,   // Smaller lakes
  8: 8,   // Even smaller
  9: 10,  // Almost all
}

/**
 * Filter lakes based on zoom level.
 * Returns lakes that should be visible at the given zoom.
 */
export function filterLakesByZoom(lakes: LakeData[], zoom: number): LakeData[] {
  // Clamp zoom to our threshold range
  const effectiveZoom = Math.min(Math.max(5, Math.floor(zoom)), 9)
  const maxScalerank = SCALERANK_THRESHOLDS[effectiveZoom] ?? 10

  return lakes.filter((lake) => lake.scalerank <= maxScalerank)
}

/**
 * Get the scalerank threshold for a given zoom level.
 * Useful for debugging.
 */
export function getScalerankThreshold(zoom: number): number {
  const effectiveZoom = Math.min(Math.max(5, Math.floor(zoom)), 9)
  return SCALERANK_THRESHOLDS[effectiveZoom] ?? 10
}
